import type { ActivePriceList } from 'modules/supplier/pricelist/types';
import type {
  DistributionMode,
  RadioPackageCatalog,
  RadioPackageCatalogOption,
  RadioPackagePricing,
  RowPackage,
} from '../types';

export type CatalogSelection = {
  package: RadioPackageCatalog;
  option: RadioPackageCatalogOption;
};

/**
 * The catalog entry a row's package selection points at. Undefined when nothing
 * is selected, the catalog has not loaded, or the selection no longer resolves
 * against it — an unresolvable selection is silent, consistent with how an
 * orphaned rate side behaves.
 */
export function findSelectedOption(
  catalog: ActivePriceList<'RADIO'> | null | undefined,
  rowPackage: RowPackage | null | undefined,
): CatalogSelection | undefined {
  if (!rowPackage || !catalog) return undefined;
  const found = catalog.packages.find((candidate) => candidate.id === rowPackage.id);
  const option = found?.options.find((candidate) => candidate.id === rowPackage.option.id);
  return found && option ? { package: found, option } : undefined;
}

/** Sentence case, as the package panel's tag reads it. Places that render it as
 * a note lowercase it in CSS rather than carrying a second wording. */
export const packageKindLabel = (kind: RadioPackagePricing['kind']): string =>
  kind === 'RATE_CARD' ? 'Rate card' : 'Discount';

/** Package and option in one line, wherever both are shown as plain text. */
export const formatPackageOption = (rowPackage: RowPackage): string => `${rowPackage.name} · ${rowPackage.option.name}`;

export const distributionModeLabel = (mode: DistributionMode): string =>
  mode === 'WEEK_ALIGNED' ? 'Week aligned' : 'Period total';
